import { TerminalsService } from '../services/terminals-service';
import { TempCache } from './temp-cache';
import { PortMemosService } from '../services/portmemos-service';
import { UiStateModel, UiStateModelParam } from './ui-state-model';
import { TerminalsModelBase } from './terminals-model';
import { TerminalInfo, TimestampAndTerminal, TerminalPilotInfo, TerminalUpdateMessage, Region } from './types/terminals-model.types';

const INITIAL_REFRESH_DELAY = 60000; // One minute
const REFRESH_INTERVAL = 3600000; // One hour
const LOCALTIME_REQ_DELAY = 100;
const TEMP_CACHE_TIMEOUT = 3600000; // One hour

interface PromiseInfo
{
    promise: Promise<void>;
    resolver: (() => void)|null;
    rejecter: ((error: any) => void)|null;
    inflight: boolean;
}

const PseudoTerminals = [
    "DKR.YTM", // Repair yard
    "DKOIPOF", // Off hire
    "DKOIPON"  // On hire
];

/**
 * regionsMap
 * 
 * @description provides a map of which locations (terminal locId's) that a mapped to certain regions.
 * This is being used with the frontends Port list view where users can select full regions to see,
 * which vessels are incomming to the region and which ports / terminals are in this region.
 */
export const regionsMap: Region[] = [
   {
        key: "AFR",
        name: "AFR",
        locations: ["AFR"]
    },
    {
        key: "APA",
        name: "APA",
        locations: ['APA', 'FEA']
    }, 
    {
        key: "AMR",
        name: "Americas",
        locations: ['NAM', 'LAM']
    },
    {
        key: "EUR",
        name: 'EUR',
        locations: ['NEU', 'SEU']
    },
    {
        key: "WCA",
        name: "WCA",
        locations: ["WCA"]
    }
]

export class TerminalsModelImpl extends TerminalsModelBase {
    constructor() {
        super();

        this.loadTerminals();
        this._refreshTerminals = this._refreshTerminals.bind(this);

        UiStateModel?.addEventListener(`${UiStateModelParam.PORT}-change`, () => this.dispatchEvent(new Event('terminals-updated')));
    }

    storeTerminals() {
        const arr = Array.from(this._terminals.entries());

        // Intermediate soution to prevent runtime panic
        // while we are solving out localStorage usage issue.
        try {
            localStorage.setItem(this._localStorageId, JSON.stringify(arr));
        } catch (err) {
            console.error(err)
        }
    }

    loadTerminals() {
        try {
            const res = JSON.parse(localStorage.getItem(this._localStorageId) || "null");
            if (!res) {
                this._refreshTerminals();
                return;
            }

            this._terminals = new Map(res);
            this._terminals.forEach(this._addTerminalToCity);

            this.setCacheLifecycle();
            this.dispatchEvent(new Event('terminals-updated'));

            // Terminals are not expected to change often, so refresh them a minute after startup
            setTimeout(() => { this._refreshTerminals(); }, INITIAL_REFRESH_DELAY);
        }
        catch (error) {
            this._refreshTerminals();
        }
    }

    get terminals() {
        return this._terminals;
    }

    get terminalsByCity() {
        return this._terminalsByCity;
    }

    getTerminal(locationId: string) {
        return this._terminals.get(locationId);
    }

    getTerminalsByCity(cityId: string): TerminalInfo[] | undefined {
        return this._terminalsByCity.get(cityId);
    }

    getCity(locationId: string): string|undefined {
        const terminal = this.getTerminal(locationId);

        if (terminal) {
            return terminal.city;
        }
    }

    async getTerminalLocaltime(locationId: string, utc: string): Promise<string> {

        if (PseudoTerminals.includes(locationId))
        {
            return utc;
        }

        const cacheValue = this._localTimeCache.get(this._getTimeCacheKey(locationId, utc));
        if (cacheValue)
        {
            return cacheValue;
        }

        this._addLocaltimeRequest(locationId, utc);

        await this._localTimeReqPromise.promise;

        return this._localTimeCache.get(this._getTimeCacheKey(locationId, utc))!;
    }

    async getTerminalLocaltimeBatch(timestampsAndTerminals: TimestampAndTerminal[]) {

        // Find and request all entries not in cache
        for (const entry of timestampsAndTerminals)
        {
            if (!PseudoTerminals.includes(entry.terminalId) && !this._localTimeCache.has(this._getTimeCacheKey(entry.terminalId, entry.timestamp)))
            {
                this._addLocaltimeRequest(entry.terminalId, entry.timestamp);
            }
        }

        // Wait for cache to get populated with the new values
        await this._localTimeReqPromise.promise;

        const result: string[] = [];
        for (const entry of timestampsAndTerminals)
        {
            if (PseudoTerminals.includes(entry.terminalId))
            {
                result.push(entry.timestamp);
            }
            else
            {
                result.push(this._localTimeCache.get(this._getTimeCacheKey(entry.terminalId, entry.timestamp))!);
            }
        }

        return result;
    }

    async getUTCFromTerminalLocaltime(locationId: string, local: string): Promise<string> {
        const cacheValue = this._utcTimeCache.get(this._getTimeCacheKey(locationId, local));
        if (cacheValue)
        {
            return cacheValue;
        }

        const result = await TerminalsService.getUTCFromTerminalLocaltime(locationId, local);
        
        this._utcTimeCache.set(this._getTimeCacheKey(locationId, local), result);

        return result;
    }

    async getTerminalPortMemo(locationId: string) {
        if (PortMemosService.serviceAvailable()) {
            return await PortMemosService.getTerminalPortMemo(locationId);
        } else {
            return await TerminalsService.getTerminalPortMemo(locationId);
        }
    }

    async getTerminalPilotInfo(locationId: string): Promise<TerminalPilotInfo> {
        if (!this._terminalPilotInfos.has(locationId)) {
            const pilotInfo = await TerminalsService.getTerminalPilotInfo(locationId);
            this._terminalPilotInfos.set(locationId, {
                timeToPilotInSeconds: pilotInfo.timeToPilotInSeconds,
                timeToBerthInSeconds: pilotInfo.timeToBerthInSeconds
            });
        }

        return this._terminalPilotInfos.get(locationId)!;
    }

    async createTerminal(newTerminal: TerminalUpdateMessage): Promise<void>
    {
        await TerminalsService.createTerminal(newTerminal);
    
        let terminal: TerminalInfo = {
            locationId: newTerminal.locationId,
            location: newTerminal.location,
            latitude: newTerminal.latitude,
            longitude: newTerminal.longitude,
            city: newTerminal.city,
            cityId: "Unknown",
            countryId: "",
            olsonTimezone: newTerminal.olsonTimezone,
        }

        this._terminals.set(terminal.locationId, terminal);
        this._addTerminalToCity(terminal);
        this.storeTerminals();
        this.dispatchEvent(new Event('terminals-updated'));
    }

    private _addTerminalToCity(terminal: TerminalInfo): void {
        if (!terminal.cityId || !terminal.cityId.length) return;

        if (this._terminalsByCity.has(terminal.cityId)) {
            const city = this._terminalsByCity.get(terminal.cityId);
            const terminalIndex = city!.findIndex((terminalInfo) => terminalInfo.locationId === terminal.locationId);
            if (terminalIndex !== -1) {
                // if the terminal is already known, replace with the updated info
                city!.splice(terminalIndex, 1);
            }
            city?.push(terminal);
        } else {
            this._terminalsByCity.set(terminal.cityId, [terminal])
        }
    }

    isPseudoTerminal(locationId: string): boolean
    {
        return PseudoTerminals.includes(locationId);
    }

    private _terminals: Map<string, TerminalInfo> = new Map();
    private _terminalsByCity: Map<string, TerminalInfo[]> = new Map();
    private _terminalPilotInfos: Map<string, TerminalPilotInfo> = new Map();
    private readonly _localStorageId: string = 'terminals-model';
    private _localTimeCache: TempCache<string, string> = new TempCache(TEMP_CACHE_TIMEOUT);
    private _utcTimeCache: TempCache<string, string> = new TempCache(TEMP_CACHE_TIMEOUT);
    private _localTimeRequests: TimestampAndTerminal[] = [];
    private _localTimeReqTimer: number = 0;
    private _localTimeReqPromise: PromiseInfo = { promise: Promise.resolve(), resolver: null, rejecter: null, inflight: false };

    private async _refreshTerminals() {
        const data = await TerminalsService.fetchAllTerminalsData();

        data.forEach( (terminal: TerminalInfo) => {
            this._terminals.set(terminal.locationId, terminal);
            this._addTerminalToCity(terminal);
        });

        
        this.storeTerminals();
        this.setCompleteLifecycle();
        this.dispatchEvent(new Event('terminals-updated'));

        // Refresh again in an hour
        setTimeout(() => { this._refreshTerminals(); }, REFRESH_INTERVAL);
    }

    private _addLocaltimeRequest(locationId: string, utc: string)
    {
        if (this._localTimeReqTimer)
        {
            clearTimeout(this._localTimeReqTimer);
            this._localTimeReqTimer = 0;
        }

        this._localTimeRequests.push({
            terminalId: locationId,
            timestamp: utc
        });

        if (!this._localTimeReqPromise.resolver)
        {
            // Create new promise to resolve when the actual request has been done
            this._localTimeReqPromise.promise = new Promise( (resolve, reject) => {
                this._localTimeReqPromise.resolver = resolve;
                this._localTimeReqPromise.rejecter = reject;
            });
        }

        if (!this._localTimeReqPromise.inflight)
        {
            this._localTimeReqTimer = setTimeout( () => this._doLocalTimeRequest(), LOCALTIME_REQ_DELAY );
        }
    }

    private async _doLocalTimeRequest()
    {
        this._localTimeReqTimer = 0;
        this._localTimeReqPromise.inflight = true;

        while (this._localTimeRequests.length > 0)
        {
            const data = this._localTimeRequests;
            this._localTimeRequests = [];
            try
            {
                const results = await TerminalsService.getTerminalLocaltimeBatch(data);
                for (let i = 0; i < results.length; i++)
                {
                    const cacheKey = this._getTimeCacheKey(data[i].terminalId, data[i].timestamp);
                    this._localTimeCache.set(cacheKey, results[i]);
                }
            }
            catch (err)
            {
                this._localTimeRequests = [];
                this._localTimeReqPromise.inflight = false;
                this._localTimeReqPromise.rejecter!(err);
                this._localTimeReqPromise.rejecter = null;
                this._localTimeReqPromise.resolver = null;
                return;
            }
        }

        this._localTimeReqPromise.inflight = false;
        this._localTimeReqPromise.resolver!();
        this._localTimeReqPromise.rejecter = null;
        this._localTimeReqPromise.resolver = null;
    }

    private _getTimeCacheKey(locationId: string, time: string)
    {
        return `${locationId}${time}`;
    }
}
